// Hotel data layer — erp-core /v1/erp/hotel/* through erpApi (bearer +
// X-Business-Id + X-Branch-Id = the property; RFC 9457 → ApiError). Wire
// shapes exactly as erp-core docs/API.md documents them (sub-plan 2A); every
// figure is the server's (rule 7) — nothing here prices, counts or sums.
// This task is the scaffold (keys, the room inventory shapes the fixture is
// typed against, the two inventory reads); each later 2B task adds its
// screen's calls next to these. Rate plans live in booking
// (`/api/booking/v1/business/{bid}/rate-plans*`, bookingApi) — 2B-12.

import { erpApi } from '@/lib/api';

/** TanStack Query keys `['hotel', resource, branch, …]`; a null branch = the main property. */
export const hotelKeys = {
  all: ['hotel'] as const,
  resource: (resource: string, branchId: string | null, ...parts: ReadonlyArray<string | number | boolean | null | undefined>) =>
    ['hotel', resource, branchId ?? 'main', ...parts] as const,
};

/** hotel_rooms.hk_status (housekeeping). */
export type HkStatus = 'clean' | 'cleaning' | 'dirty' | 'inspect' | 'maintenance';

/** The seven today-grid tiles (hotel/rooms Tile: occupancy × housekeeping × the business date). */
export type TileState = 'occupied' | 'due_out' | 'arriving' | 'vacant_clean' | 'vacant_dirty' | 'inspect' | 'maintenance';

/** Booking's stay status (`pending` = HANDOFF §17's tentative hold); a maintenance bar says `maintenance`. */
export type StayStatus = 'pending' | 'confirmed' | 'in_house' | 'checked_out' | 'no_show' | 'cancelled' | 'maintenance';

/** HANDOFF §17 «Status → pill» (the tape bar / list pill), as erp-core rooms.Pill computes it. */
export type StayPill = 'tentative' | 'confirmed' | 'in_house' | 'due_out' | 'arriving' | 'no_show' | 'block' | 'ota' | 'maintenance';

/** HANDOFF §17 reservation.source; a maintenance bar says `maintenance`. */
export type StaySource = 'phone' | 'walk_in' | 'app' | 'ota' | 'company' | 'maintenance';

/** GET /hotel/tape `rooms[]` — one row per physical room of the property. */
export interface TapeRoom {
  unit_id: string;
  /** The room number as the owner typed it («101»); the UI renders the digits. */
  label: string;
  floor: number | null;
  unit_type_id: string;
  unit_type_name: string;
  /** single · double · triple · quad · suite · family · other — erp-core's (derived from the type's name, else its capacity). */
  unit_type_kind?: string;
  /** erp-core's plural for the type («أجنحة» for suites, «غرف مزدوجة» …): shown as served, never composed here. */
  unit_type_plural_label?: string;
  /** The floor as erp-core words it («الطابق الأول»); a payload without it falls back to the numeric floor. */
  floor_label?: string;
  hk_status: HkStatus;
}

/** `unit_types[]` of the tape and today reads. */
export interface UnitTypeInfo {
  id: string;
  name: string;
  kind: string;
  plural_label: string;
}

/** GET /hotel/tape `nights[]` — booking's ARI per night (available excludes closed rooms; warn at ≥ 95 %). */
export interface TapeNight {
  night: string;
  occupied: number;
  available: number;
  pct: number;
  warn: boolean;
}

/** GET /hotel/tape `stays[]` — one bar per room segment. */
export interface TapeStay {
  id: string;
  unit_id: string;
  from: string;
  to: string;
  status: StayStatus;
  pill: StayPill;
  source: StaySource;
  guest_name: string;
  company_name?: string;
  channel?: string;
}

export interface TapeResponse {
  unit_types?: UnitTypeInfo[];
  rooms: TapeRoom[];
  nights: TapeNight[];
  stays: TapeStay[];
  summary: { night_pct: number; occupied: number; available: number; arrivals: number; departures: number };
  business_date: string;
}

/** GET /hotel/rooms/today `floors[].rooms[]` — one tile per room. */
export interface TodayRoom {
  unit_id: string;
  label: string;
  unit_type_name: string;
  unit_type_kind?: string;
  unit_type_plural_label?: string;
  tile: TileState;
  /** «حسين م.» — front desk only. */
  guest_short?: string;
  stay_id?: string;
  /** The room's housekeeping state, on every tile (an arrival's tile hides it otherwise); the check-in reads it instead of a second room-panel read. */
  hk_status?: HkStatus;
}

export interface TodayFloor {
  floor: number | null;
  label: string;
  rooms: TodayRoom[];
}

export interface RoomsTodayResponse {
  business_date: string;
  unit_types?: UnitTypeInfo[];
  counts: { occupied: number; due_out: number; arriving: number; vacant_clean: number; vacant_dirty: number; inspect: number; maintenance: number };
  readiness: { ready: number; needed: number };
  floors: TodayFloor[];
}

/** GET /hotel/rooms/{unitId} `room`. */
export interface PanelRoom {
  unit_id: string;
  label: string;
  floor: number | null;
  unit_type_id: string;
  unit_type_name: string;
  unit_type_kind?: string;
  unit_type_plural_label?: string;
}

/** GET /hotel/rooms/{unitId} `hk` — the housekeeping line (`task` «daily» by default). */
export interface PanelHK {
  status: HkStatus | 'cleaning';
  assignee_name?: string;
  note: string;
  task: string;
}

/** GET /hotel/rooms/{unitId} `stay` — guest, identity and money fields only for the front desk. */
export interface PanelStay {
  id: string;
  pill: StayPill;
  stay_from: string;
  stay_to: string;
  nights_count: number;
  adults: number;
  children?: number;
  keys: number;
  guest_name?: string;
  customer_phone?: string;
  /** The server's document label — MASKED by erp-core (ruling D-2: «جواز عراقي · ···567») — shown exactly as served, never completed or reformatted. */
  primary_document?: string;
  stays_count?: number;
  plan_name?: string;
  night_price_minor?: number;
  source?: StaySource;
  booked_by_name?: string;
  /** erp-core serves the guest folio and its balance INSIDE `stay` (API.md rooms/{unit}); preferred over the reservation read's folios. */
  folio_id?: string;
  balance_minor?: number;
}

export interface RoomPanelResponse {
  business_date: string;
  room: PanelRoom;
  tile: TileState;
  hk: PanelHK;
  stay?: PanelStay | null;
  notes?: string;
  /** The in-house stay's guest folio (erp-core adds it to the panel — 2C-3 ruling 1); absent ⇒ the reservation read's folios. */
  folio_id?: string;
}

/** GET /hotel/reservations/{id} `folios[]`. */
export interface FolioSummary {
  id: string;
  label: string;
  payer: 'guest' | 'company';
  status: 'open' | 'closed';
  balance_minor: number;
}

/** GET /hotel/reservations/{id}. */
export interface ReservationView {
  stay: StayView;
  extension: {
    booked_by_name?: string;
    notes: string;
    keys_issued: number;
    late_checkout_until_min?: number;
    /** The desk deposit still held on 2300 (extension columns of erp-core stays.Extension). */
    deposit_held_minor?: number;
    deposit_method?: string;
    deposit_taken_at?: string;
    deposit_applied_entry_id?: string;
    online_deposit_entry_id?: string;
    checked_in_at?: string;
  };
  folios: FolioSummary[];
  /** The detail read's additions (each rendered only when the server sends it). */
  pill?: StayPill;
  company_name?: string;
  folio_id?: string;
  /** Booking's free-cancel deadline (an instant). */
  free_cancel_until?: string;
  /** Sent beside the deadline (whether it came from the business policy); parsed leniently, drawn nowhere. */
  free_cancel_derived?: boolean;
  free_cancel_policy?: unknown;
  guests?: ReservationGuest[];
  /** The held deposit as the folio read names it: «عربون الحجز · {method}». */
  held_deposit?: HeldDeposit;
  /** The server's amount to take at the desk now, when it computes one. */
  collect_due_minor?: number;
}

/** A registered guest of a stay (identity only as the server's masked label — never a number). */
export interface ReservationGuest {
  name: string;
  relation?: string;
  is_primary?: boolean;
  is_minor?: boolean;
  /** MASKED by the server: kind + the last three characters («جواز عراقي · ···567») — shown as given, never a full number. */
  document_label?: string;
}

export interface HeldDeposit {
  amount_minor: number;
  method?: string;
  taken_at?: string;
  label?: string;
}

/** bookingclient.Stay as the stay writes answer it (the fields the tape reads back). */
export interface StayView {
  id: string;
  unit_id?: string | null;
  unit_label?: string;
  segments?: Array<{ unit_id: string; from: string; to: string }>;
  stay_from: string;
  stay_to: string;
  nights_count?: number;
  status: string;
  source?: string;
  /** The OTA provider's name («Booking») — nested under the stay, as bookingclient.Stay serves it. */
  channel?: string;
  customer_name?: string;
  /** bookingclient.Stay — the fields the reservation detail and the check-in read (all optional on the wire). */
  unit_type_id?: string;
  unit_type_name?: string;
  customer_phone?: string;
  adults?: number;
  children?: number;
  arrived_at?: string;
  total_minor?: number;
  deposit_due_minor?: number;
  deposit_paid_minor?: number;
  nights?: Array<{ night: string; price_minor: number }>;
  /** bookingclient.Stay: the hold's expiry; a pending stay WITHOUT one is awaiting its deposit, not a tentative hold. */
  tentative_until?: string | null;
}

/** GET /hotel/quote `types[]` — one card per room type (free = the minimum over the nights; price = the plan's first night, null when the plan does not price every night). */
export interface QuoteType {
  unit_type_id: string;
  name: string;
  free: number;
  price_minor: number | null;
  usd_cents: number | null;
  plan_name: string;
  /** «آخر الغرف»: 0 < free ≤ 2. */
  last_rooms: boolean;
}

/** GET /hotel/quote `plans[]` — the active plans the desk may pick. */
export interface QuotePlan {
  id: string;
  kind: string;
  name: string;
  deposit_pct?: number;
  free_cancel_days: number;
  breakfast: boolean;
  min_nights: number;
}

/** GET /hotel/quote — the totals are the selected type's (zero / null until a type is quoted). */
export interface QuoteResponse {
  types: QuoteType[];
  plans: QuotePlan[];
  plan_id: string;
  plan_name: string;
  deposit_minor: number;
  /** The plan's deposit rule (R1: percent of the first night); null under rack with the type's own rule. */
  deposit_pct: number | null;
  total_minor: number;
  total_usd_cents: number | null;
  breakfast: boolean;
  nights: number;
}

export interface QuoteQuery {
  from: string;
  to: string;
  adults: number;
  children: number;
  plan_id?: string;
  unit_type_id?: string;
  /** The company the reservation is for (source «شركة»): its contract prices the nights. */
  company_customer_id?: string;
}

/** The subset of a `GET /hotel/companies` row the pickers read (the list screen reads the whole `CompanyListItem`). */
export interface CompanyItem {
  customer_id: string;
  name: string;
  has_terms?: boolean;
  remaining_minor?: number;
}

/** The desk sources (erp-core frontdesk deskSources). */
export type DeskSource = 'phone' | 'walk_in' | 'company';

/** POST /hotel/reservations — an existing guest (`customer_id` + name + phone) or `new_customer`. */
export interface CreateReservationBody {
  customer_id?: string;
  customer_name?: string;
  customer_phone?: string;
  new_customer?: { name: string; phone: string };
  unit_type_id: string;
  from: string;
  to: string;
  adults: number;
  children?: number;
  source: DeskSource;
  rate_plan_id?: string;
  tentative: boolean;
  /** Source «شركة»: the company the stay is booked for (erp-core applies its contract nights and records it on the stay). */
  company_customer_id?: string;
}

export interface CreateReservationResponse {
  stay_id: string;
  folio_id: string;
  stay: StayView;
}

/** GET /hotel/reservations `items[]`. */
export interface ReservationListItem {
  id: string;
  guest_name: string;
  phone?: string;
  unit_label: string;
  unit_type_name: string;
  stay_from: string;
  stay_to: string;
  nights: number;
  status: string;
  arrived: boolean;
  tentative: boolean;
  source: string;
  adults: number;
  children?: number;
  deposit_due_minor: number;
  deposit_paid_minor: number;
  total_minor: number;
  /** HANDOFF §17 status → pill, as the tape computes it (the list reads the same rule). */
  pill?: StayPill;
  /** The OTA provider's name («Booking»), when booking knows it. */
  channel?: string;
  /** The company the reservation was made for. */
  company_name?: string;
  /** The guest folio's balance; null when the stay has no open guest folio. Absent ⇒ the server does not send it (the «الرصيد» column is not drawn). */
  balance_minor?: number | null;
}

export interface ReservationListQuery {
  view: 'arrivals' | 'departures' | 'all';
  date?: string;
  to?: string;
  q?: string;
}

const enc = encodeURIComponent;

export const hotelApi = {
  /** The tape: the business date + 14 nights by default, at most 31 (`400 invalid_range`). */
  tape: (q: { from?: string; to?: string } = {}) => erpApi.get<TapeResponse>('/hotel/tape', q),
  /** The today grid (front desk or housekeeping). */
  roomsToday: () => erpApi.get<RoomsTodayResponse>('/hotel/rooms/today'),
  /** The room panel (404 for a unit that is not a room of the property). */
  room: (unitId: string) => erpApi.get<RoomPanelResponse>(`/hotel/rooms/${enc(unitId)}`),
  /** The reservation with its folios (the panel's «فتح الفوليو» and «رصيد الفوليو»). */
  reservation: (stayId: string) => erpApi.get<ReservationView>(`/hotel/reservations/${enc(stayId)}`),
  /**
   * Move a stay to another room from a night (`from_date`, always sent — the
   * dropped column; never before the business date). 409 room_not_ready ·
   * unit_taken · prior_business_date pass through as ApiError. `request_id` (one per intent, ./data/intent): a repeat answers the same stay.
   */
  moveRoom: (stayId: string, body: { unit_id: string; from_date: string; request_id: string }) => erpApi.post<StayView>(`/hotel/stays/${enc(stayId)}/move-room`, body),
  /** Extend by whole nights in the stay's last room (409 unit_taken when the next night is held). */
  extend: (stayId: string, nights: number, requestId: string) => erpApi.post<StayView>(`/hotel/stays/${enc(stayId)}/extend`, { nights, request_id: requestId }),
  /** Every type's free count and price under a plan; the totals and the deposit for `unit_type_id` (2B-4). */
  quote: (q: QuoteQuery) => erpApi.get<QuoteResponse>('/hotel/quote', { ...q }),
  /** Book a type (tentative = «حفظ كمبدئي»); 409 no_unit_free · 422 min_nights · … pass through as ApiError. */
  createReservation: (body: CreateReservationBody) => erpApi.post<CreateReservationResponse>('/hotel/reservations', body),
  /** The reservations lists; `q` searches name, phone and room (the hotel top bar). */
  reservations: (q: ReservationListQuery) => erpApi.get<{ items: ReservationListItem[] }>('/hotel/reservations', { ...q }),
};
